package com.elan.edubridge.ui.staff

import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.model.AcademyAttendanceStats
import com.elan.edubridge.shared.domain.model.AttendanceStats
import com.elan.edubridge.shared.domain.model.ClassAttendanceStat
import com.elan.edubridge.shared.domain.model.DailyAttendanceStat
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.shared.domain.model.StudentAttendanceStat
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.icon
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.Element
import org.w3c.dom.HTMLFormElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLElement
import kotlin.js.Date
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.round
import kotlin.math.sin

/** `/app/{academyId}/stats` — 기간 원장을 한 번 읽어 학원·반·학생별 출결을 함께 보여준다. */
object StatsPage {
    private val scope = MainScope()
    private val getAttendanceStats = SharedGraph.createGetAttendanceStatsUseCase()

    private data class DateRange(val startDate: String, val endDate: String)

    fun open(session: SessionState, academyId: String) = load(session, academyId, defaultRange())

    private fun load(session: SessionState, academyId: String, range: DateRange) {
        renderLoading(session, academyId, range)
        scope.launch {
            val stats = getAttendanceStats(academyId, range.startDate, range.endDate, classId = null)
            renderLoaded(session, academyId, range, stats)
        }
    }

    private fun renderLoading(session: SessionState, academyId: String, range: DateRange) {
        DashboardShell.render(session, academyId, "stats", pageHeaderHtml(range) + """
            <section class="eb-card" aria-live="polite">
                ${icon("icon-refresh", "md", "eb-spin")}출결 통계를 불러오는 중이에요.
            </section>
        """.trimIndent()) {
            wireDateRange(session, academyId)
        }
    }

    private fun renderLoaded(
        session: SessionState,
        academyId: String,
        range: DateRange,
        stats: AcademyAttendanceStats,
    ) {
        DashboardShell.render(session, academyId, "stats", pageHeaderHtml(range) + contentHtml(stats)) {
            wireDateRange(session, academyId)
            wireChartTooltips()
        }
    }

    private fun pageHeaderHtml(range: DateRange): String = """
        <div class="eb-page-header">
            <div>
                <h1 class="eb-page-title">통계</h1>
                <p class="eb-meta">선택한 기간의 출결 원장을 기준으로 집계해요.</p>
            </div>
        </div>
        <form class="eb-toolbar" id="stats-date-range-form">
            <div class="eb-field">
                <label for="stats-start-date">시작일</label>
                <input id="stats-start-date" type="date" value="${range.startDate.escapeHtml()}" required>
            </div>
            <div class="eb-field">
                <label for="stats-end-date">종료일</label>
                <input id="stats-end-date" type="date" value="${range.endDate.escapeHtml()}" required>
            </div>
            <button class="eb-button eb-button--primary" id="stats-date-range-submit" type="submit">조회</button>
            <p class="eb-form-error" id="stats-date-range-error" role="alert" hidden></p>
        </form>
    """.trimIndent()

    private fun contentHtml(stats: AcademyAttendanceStats): String = """
        ${overviewHtml(stats.overall)}
        ${statusCardHtml(stats.overall)}
        ${if (stats.overall.totalCount == 0) emptyStatsHtml() else detailsHtml(stats)}
    """.trimIndent()

    private fun overviewHtml(stats: AttendanceStats): String = """
        <section class="eb-stat-tiles" aria-label="학원 출결 요약">
            <div class="eb-stat-tile"><span class="eb-stat-tile__label">대상 출결</span><strong class="eb-stat-tile__value">${stats.totalCount}</strong></div>
            <div class="eb-stat-tile"><span class="eb-stat-tile__label">출석 인정</span><strong class="eb-stat-tile__value eb-text-success">${stats.attendedCount}</strong></div>
            <div class="eb-stat-tile"><span class="eb-stat-tile__label">출석률</span><strong class="eb-stat-tile__value">${attendanceRateLabel(stats)}</strong></div>
            <div class="eb-stat-tile"><span class="eb-stat-tile__label">미출석</span><strong class="eb-stat-tile__value eb-text-error">${stats.absentCount + stats.notArrivedCount}</strong></div>
        </section>
    """.trimIndent()

    private fun statusCardHtml(stats: AttendanceStats): String = """
        <section class="eb-card eb-stats-chart-card" aria-label="기간 전체 현재 상태 구성">
            <div class="eb-stats-chart__heading">
                <div>
                    <h2>기간 전체 현재 상태 구성</h2>
                    <p class="eb-meta">현재 상태는 서로 겹치지 않아요. 지각 등원은 별도 지표로 확인해요.</p>
                </div>
                ${statusLegendHtml()}
            </div>
            ${statusDonutHtml(stats)}
            <div class="eb-stats-chart__chips" aria-label="상태별 수치">
                <span class="eb-chip eb-chip--success">출석 ${stats.presentCount}</span>
                <span class="eb-chip eb-chip--warning">현재 지각 ${stats.lateCount}</span>
                <span class="eb-chip eb-chip--info">하원 ${stats.leftCount}</span>
                <span class="eb-chip eb-chip--error">결석 ${stats.absentCount}</span>
                <span class="eb-chip eb-chip--error">미등원 ${stats.notArrivedCount}</span>
            </div>
            <p class="eb-meta eb-stats-chart__late-arrival">기간 지각 등원 <strong>${stats.lateArrivalCount}건</strong> · 지각률 ${stats.lateRate?.let(::formatPercent) ?: "데이터 없음"}</p>
        </section>
    """.trimIndent()

    private fun emptyStatsHtml(): String = """
        <section class="eb-card">
            <div class="eb-empty-state">
                ${icon("icon-stats", "xl")}
                <h2>선택한 기간의 출결 기록이 없어요</h2>
                <p>기간을 조정하거나 출결 기록이 생성된 뒤 다시 조회해 주세요.</p>
            </div>
        </section>
    """.trimIndent()

    private fun detailsHtml(stats: AcademyAttendanceStats): String = """
        ${dailyTrendChartHtml(stats.byDate)}
        ${dailyStatusCompositionChartHtml(stats.byDate)}
        ${dailyTableHtml(stats.byDate)}
        ${comparisonChartHtml("반별 출석률 비교", "반", stats.byClass.map { ComparisonEntry(it.className, it.stats) }, "class")}
        ${classTableHtml(stats.byClass)}
        ${comparisonChartHtml("학생별 출석률 비교", "학생", stats.byStudent.map { ComparisonEntry(it.studentName, it.stats) }, "student")}
        ${studentTableHtml(stats.byStudent)}
    """.trimIndent()

    /** 차트는 기존 집계 결과를 재표현할 뿐, 추가 조회나 별도 집계를 만들지 않는다. */
    private data class ComparisonEntry(val label: String, val stats: AttendanceStats)

    private data class StatusEntry(
        val label: String,
        val value: Int,
        val variant: String,
    )

    private fun currentStatusEntries(stats: AttendanceStats): List<StatusEntry> = listOf(
        StatusEntry("출석", stats.presentCount, "success"),
        StatusEntry("지각", stats.lateCount, "warning"),
        StatusEntry("하원", stats.leftCount, "info"),
        StatusEntry("결석·미등원", stats.absentCount + stats.notArrivedCount, "error"),
    )

    private fun statusLegendHtml(): String = """
        <div class="eb-stats-chart__legend" aria-label="상태 범례">
            <span class="eb-stats-chart__legend-item"><i class="eb-stats-chart__legend-mark eb-stats-chart__legend-mark--success" aria-hidden="true"></i>출석</span>
            <span class="eb-stats-chart__legend-item"><i class="eb-stats-chart__legend-mark eb-stats-chart__legend-mark--warning" aria-hidden="true"></i>지각</span>
            <span class="eb-stats-chart__legend-item"><i class="eb-stats-chart__legend-mark eb-stats-chart__legend-mark--info" aria-hidden="true"></i>하원</span>
            <span class="eb-stats-chart__legend-item"><i class="eb-stats-chart__legend-mark eb-stats-chart__legend-mark--error" aria-hidden="true"></i>결석·미등원</span>
        </div>
    """.trimIndent()

    private fun statusDonutHtml(stats: AttendanceStats): String {
        val entries = currentStatusEntries(stats).filter { it.value > 0 }
        if (entries.size !in 3..5) {
            return chartEmptyHtml("도넛은 세 가지 이상의 현재 상태가 있을 때 표시해요. 아래 상태별 수치에서 정확한 값을 확인해 주세요.")
        }

        val total = entries.sumOf(StatusEntry::value).toDouble()
        val centerX = 128.0
        val centerY = 124.0
        val outerRadius = 88.0
        val innerRadius = 54.0
        var cursor = -PI / 2.0
        val slices = entries.joinToString("\n") { entry ->
            val sweep = entry.value / total * PI * 2.0
            val gap = minOf(2.0 / ((outerRadius + innerRadius) / 2.0), sweep * 0.25)
            val path = donutSlicePath(centerX, centerY, outerRadius, innerRadius, cursor + gap / 2.0, cursor + sweep - gap / 2.0)
            cursor += sweep
            val tooltip = "${entry.label} ${entry.value}건"
            """
                <g class="eb-stats-chart__mark" tabindex="0" role="img" aria-label="${tooltip.escapeHtml()}" data-stats-tooltip="${tooltip.escapeHtml()}" data-stats-tooltip-target="stats-status-tooltip">
                    <path class="eb-stats-chart__donut-slice eb-stats-chart__bar--${entry.variant}" d="$path"/>
                </g>
            """.trimIndent()
        }
        val directLabels = entries.mapIndexed { index, entry ->
            val y = 56.0 + index * 36.0
            """
                ${statusMarkerSvg(entry.variant, 252.0, y - 5.0)}
                <text class="eb-stats-chart__value" x="266" y="$y">${entry.label.escapeHtml()} ${entry.value}건</text>
            """.trimIndent()
        }.joinToString("\n")
        return """
            <div class="eb-stats-chart eb-stats-chart--donut">
                <svg viewBox="0 0 520 248" role="img" aria-labelledby="stats-status-chart-title stats-status-chart-description">
                    <title id="stats-status-chart-title">기간 전체 현재 상태 구성</title>
                    <desc id="stats-status-chart-description">현재 상태를 출석, 지각, 하원, 결석과 미등원으로 나눈 도넛 그래프입니다.</desc>
                    $slices
                    <text class="eb-stats-chart__donut-center-label" x="$centerX" y="${centerY - 8}">출석률</text>
                    <text class="eb-stats-chart__donut-center-value" x="$centerX" y="${centerY + 16}">${attendanceRateLabel(stats)}</text>
                    $directLabels
                </svg>
                <p class="eb-stats-chart__tooltip" id="stats-status-tooltip" role="tooltip" aria-live="polite" hidden></p>
            </div>
        """.trimIndent()
    }

    private fun statusMarkerSvg(variant: String, x: Double, y: Double): String = when (variant) {
        "success" -> """<circle class="eb-stats-chart__state-marker eb-stats-chart__state-marker--$variant" cx="$x" cy="$y" r="5"/>"""
        "warning" -> """<rect class="eb-stats-chart__state-marker eb-stats-chart__state-marker--$variant" x="${x - 5}" y="${y - 5}" width="10" height="10"/>"""
        "info" -> """<polygon class="eb-stats-chart__state-marker eb-stats-chart__state-marker--$variant" points="$x,${y - 6} ${x + 6},$y $x,${y + 6} ${x - 6},$y"/>"""
        else -> """<polygon class="eb-stats-chart__state-marker eb-stats-chart__state-marker--$variant" points="$x,${y - 6} ${x + 6},${y + 5} ${x - 6},${y + 5}"/>"""
    }

    private fun donutSlicePath(
        centerX: Double,
        centerY: Double,
        outerRadius: Double,
        innerRadius: Double,
        startAngle: Double,
        endAngle: Double,
    ): String {
        fun point(radius: Double, angle: Double): Pair<Double, Double> =
            centerX + radius * cos(angle) to centerY + radius * sin(angle)

        val (outerStartX, outerStartY) = point(outerRadius, startAngle)
        val (outerEndX, outerEndY) = point(outerRadius, endAngle)
        val (innerEndX, innerEndY) = point(innerRadius, endAngle)
        val (innerStartX, innerStartY) = point(innerRadius, startAngle)
        val largeArc = if (endAngle - startAngle > PI) 1 else 0
        return "M$outerStartX,$outerStartY A$outerRadius,$outerRadius 0 $largeArc 1 $outerEndX,$outerEndY L$innerEndX,$innerEndY A$innerRadius,$innerRadius 0 $largeArc 0 $innerStartX,$innerStartY Z"
    }

    private fun dailyStatusCompositionChartHtml(rows: List<DailyAttendanceStat>): String {
        val values = rows.filter { it.stats.totalCount > 0 }
        val body = if (values.isEmpty()) {
            chartEmptyHtml("일자별 상태 구성을 표시할 출결 데이터가 없어요.")
        } else {
            dailyStatusCompositionSvg(values)
        }
        return """
            <section class="eb-card eb-stats-chart-card" aria-labelledby="stats-daily-status-title">
                <div class="eb-stats-chart__heading">
                    <div>
                        <h2 id="stats-daily-status-title">일자별 상태 구성</h2>
                        <p class="eb-meta">하루의 현재 상태를 누적 막대로 비교해요. 막대에 마우스를 올리거나 키보드로 초점을 맞추면 수치를 확인할 수 있어요.</p>
                    </div>
                    ${statusLegendHtml()}
                </div>
                $body
            </section>
        """.trimIndent()
    }

    private fun dailyStatusCompositionSvg(rows: List<DailyAttendanceStat>): String {
        val left = 42.0
        val right = 18.0
        val top = 18.0
        val bottom = 42.0
        val height = 250.0
        val slotWidth = 28.0
        val width = maxOf(620.0, left + right + rows.size * slotWidth)
        val plotWidth = width - left - right
        val plotHeight = height - top - bottom
        val maxTotal = rows.maxOf { it.stats.totalCount }.coerceAtLeast(1)
        val resolvedSlotWidth = plotWidth / rows.size
        val barWidth = minOf(24.0, (resolvedSlotWidth - 4.0).coerceAtLeast(8.0))
        val labelInterval = ((rows.size + 6) / 7).coerceAtLeast(1)
        fun y(value: Int): Double = top + plotHeight * (1.0 - value.toDouble() / maxTotal)
        val grid = listOf(0, maxTotal / 2, maxTotal).distinct().joinToString("") { count ->
            val gridY = y(count)
            """<g><line class="eb-stats-chart__grid" x1="$left" x2="${width - right}" y1="$gridY" y2="$gridY"/><text class="eb-stats-chart__axis-label" x="0" y="${gridY + 4}">$count</text></g>"""
        }
        val bars = rows.mapIndexed { index, row ->
            val x = left + resolvedSlotWidth * index + (resolvedSlotWidth - barWidth) / 2.0
            var accumulated = 0
            val entries = currentStatusEntries(row.stats)
            val segments = entries.joinToString("") { entry ->
                if (entry.value == 0) return@joinToString ""
                val start = accumulated
                accumulated += entry.value
                val segmentTop = y(accumulated)
                val segmentBottom = y(start)
                val segmentHeight = (segmentBottom - segmentTop - 2.0).coerceAtLeast(0.0)
                val shape = if (accumulated == row.stats.totalCount) {
                    """<path class="eb-stats-chart__bar eb-stats-chart__bar--${entry.variant}" d="${verticalBarPath(x, segmentTop, barWidth, segmentHeight)}"/>"""
                } else {
                    """<rect class="eb-stats-chart__bar eb-stats-chart__bar--${entry.variant}" x="$x" y="$segmentTop" width="$barWidth" height="$segmentHeight"/>"""
                }
                shape
            }
            val values = entries.joinToString(", ") { "${it.label} ${it.value}건" }
            val tooltip = "${row.serviceDate}: $values"
            val label = if (index % labelInterval == 0 || index == rows.lastIndex) {
                """<text class="eb-stats-chart__x-label" x="${x + barWidth / 2.0}" y="${height - 16}">${shortDateLabel(row.serviceDate).escapeHtml()}</text>"""
            } else ""
            """
                <g class="eb-stats-chart__mark" tabindex="0" role="img" aria-label="${tooltip.escapeHtml()}" data-stats-tooltip="${tooltip.escapeHtml()}" data-stats-tooltip-target="stats-daily-status-tooltip">
                    <rect class="eb-stats-chart__hit" x="${x - 2}" y="$top" width="${barWidth + 4}" height="$plotHeight"/>
                    $segments
                    $label
                </g>
            """.trimIndent()
        }.joinToString("\n")
        return """
            <div class="eb-stats-chart eb-stats-chart--stacked">
                <div class="eb-stats-chart__scroll" tabindex="0" aria-label="일자별 상태 구성 차트, 가로로 스크롤해 모든 날짜를 확인할 수 있어요.">
                    <svg viewBox="0 0 $width $height" style="width:${width}px" role="img" aria-labelledby="stats-daily-status-svg-title stats-daily-status-svg-description">
                        <title id="stats-daily-status-svg-title">일자별 상태 구성</title>
                        <desc id="stats-daily-status-svg-description">각 날짜의 출석, 지각, 하원, 결석과 미등원 건수를 누적 막대로 나타냅니다.</desc>
                        $grid
                        $bars
                    </svg>
                </div>
                <p class="eb-stats-chart__tooltip" id="stats-daily-status-tooltip" role="tooltip" aria-live="polite" hidden></p>
            </div>
        """.trimIndent()
    }

    private fun dailyTrendChartHtml(rows: List<DailyAttendanceStat>): String {
        val values = rows.mapNotNull { row -> row.stats.attendanceRate?.let { DailyTrendPoint(row.serviceDate, it) } }
        val body = if (values.size < 2) {
            chartEmptyHtml("추이를 표시할 출결 데이터가 두 날짜 이상 필요해요.")
        } else {
            dailyTrendSvg(values)
        }
        return """
            <section class="eb-card eb-stats-chart-card" aria-labelledby="stats-daily-trend-title">
                <div class="eb-stats-chart__heading">
                    <div>
                        <h2 id="stats-daily-trend-title">일자별 출석률 추이</h2>
                        <p class="eb-meta">마커에 마우스를 올리거나 키보드로 초점을 맞추면 해당 일자의 수치를 확인할 수 있어요.</p>
                    </div>
                </div>
                $body
            </section>
        """.trimIndent()
    }

    private data class DailyTrendPoint(val date: String, val rate: Double)

    private fun dailyTrendSvg(points: List<DailyTrendPoint>): String {
        val width = 620.0
        val left = 42.0
        val right = 18.0
        val top = 18.0
        val bottom = 42.0
        val height = 240.0
        val plotWidth = width - left - right
        val plotHeight = height - top - bottom
        fun x(index: Int): Double = left + plotWidth * index / (points.size - 1)
        fun y(rate: Double): Double = top + plotHeight * (1.0 - rate.coerceIn(0.0, 1.0))
        val line = points.mapIndexed { index, point -> "${if (index == 0) "M" else "L"}${x(index)},${y(point.rate)}" }.joinToString(" ")
        val area = "$line L${x(points.lastIndex)},${top + plotHeight} L${x(0)},${top + plotHeight} Z"
        val grid = listOf(0, 25, 50, 75, 100).joinToString("") { percent ->
            val gridY = y(percent / 100.0)
            """<g><line class="eb-stats-chart__grid" x1="$left" x2="${width - right}" y1="$gridY" y2="$gridY"/><text class="eb-stats-chart__axis-label" x="0" y="${gridY + 4}">$percent%</text></g>"""
        }
        val markers = points.mapIndexed { index, point ->
            val x = x(index)
            val y = y(point.rate)
            val tooltip = "${point.date} 출석률 ${formatPercent(point.rate)}"
            """
                <g class="eb-stats-chart__mark" tabindex="0" role="img" aria-label="${tooltip.escapeHtml()}" data-stats-tooltip="${tooltip.escapeHtml()}" data-stats-tooltip-target="stats-daily-tooltip" data-stats-crosshair="stats-daily-crosshair" data-stats-crosshair-x="$x">
                    <circle class="eb-stats-chart__point-hit" cx="$x" cy="$y" r="14"/>
                    <circle class="eb-stats-chart__point" cx="$x" cy="$y" r="5"/>
                    <text class="eb-stats-chart__x-label" x="$x" y="${height - 16}">${shortDateLabel(point.date).escapeHtml()}</text>
                </g>
            """.trimIndent()
        }.joinToString("\n")
        return """
            <div class="eb-stats-chart eb-stats-chart--trend">
                <svg viewBox="0 0 $width $height" role="img" aria-labelledby="stats-daily-trend-svg-title stats-daily-trend-svg-description">
                    <title id="stats-daily-trend-svg-title">일자별 출석률 추이</title>
                    <desc id="stats-daily-trend-svg-description">선택 기간의 일자별 출석률을 선으로 나타냅니다.</desc>
                    $grid
                    <path class="eb-stats-chart__area" d="$area"/>
                    <line class="eb-stats-chart__crosshair" id="stats-daily-crosshair" x1="0" x2="0" y1="$top" y2="${top + plotHeight}" hidden/>
                    <path class="eb-stats-chart__line" d="$line"/>
                    $markers
                </svg>
                <p class="eb-stats-chart__tooltip" id="stats-daily-tooltip" role="tooltip" aria-live="polite" hidden></p>
            </div>
        """.trimIndent()
    }

    private fun comparisonChartHtml(title: String, label: String, rows: List<ComparisonEntry>, chartId: String): String {
        val body = if (rows.isEmpty()) chartEmptyHtml("비교할 ${label}별 출결 데이터가 없어요.") else comparisonChartSvg(rows, chartId)
        return """
            <section class="eb-card eb-stats-chart-card" aria-labelledby="stats-$chartId-comparison-title">
                <div class="eb-stats-chart__heading">
                    <div>
                        <h2 id="stats-$chartId-comparison-title">$title</h2>
                        <p class="eb-meta">출석률을 같은 기준으로 비교해요. 데이터가 없는 항목은 막대를 표시하지 않아요.</p>
                    </div>
                </div>
                $body
            </section>
        """.trimIndent()
    }

    private fun comparisonChartSvg(rows: List<ComparisonEntry>, chartId: String): String {
        val labelX = 160.0
        val barWidth = 370.0
        val rowHeight = 38.0
        val height = (rows.size * rowHeight + 22.0).coerceAtLeast(76.0)
        val bars = rows.mapIndexed { index, row ->
            val y = 10.0 + index * rowHeight
            val rate = row.stats.attendanceRate
            val width = rate?.coerceIn(0.0, 1.0)?.times(barWidth) ?: 0.0
            val rateLabel = rate?.let(::formatPercent) ?: "데이터 없음"
            val valueX = if (rate == null) labelX + 8.0 else labelX + width + 8.0
            val tooltip = "${row.label} 출석률 $rateLabel, 출석 인정 ${row.stats.attendedCount}건 / 대상 ${row.stats.totalCount}건"
            """
                <g class="eb-stats-chart__mark" tabindex="0" role="img" aria-label="${tooltip.escapeHtml()}" data-stats-tooltip="${tooltip.escapeHtml()}" data-stats-tooltip-target="stats-$chartId-tooltip">
                    <rect class="eb-stats-chart__hit" x="$labelX" y="${y - 7}" width="$barWidth" height="34"/>
                    <text class="eb-stats-chart__axis-label" x="0" y="${y + 13}">${shortComparisonLabel(row.label).escapeHtml()}</text>
                    <rect class="eb-stats-chart__bar-track" x="$labelX" y="$y" width="$barWidth" height="20" rx="0"/>
                    ${if (rate == null) "" else """<path class="eb-stats-chart__bar eb-stats-chart__bar--success" d="${horizontalBarPath(labelX, y, width)}"/>"""}
                    <text class="eb-stats-chart__value" x="$valueX" y="${y + 14}">$rateLabel</text>
                </g>
            """.trimIndent()
        }.joinToString("\n")
        return """
            <div class="eb-stats-chart eb-stats-chart--comparison">
                <svg viewBox="0 0 570 $height" role="img" aria-labelledby="stats-$chartId-comparison-svg-title stats-$chartId-comparison-svg-description">
                    <title id="stats-$chartId-comparison-svg-title">${chartId}별 출석률 비교</title>
                    <desc id="stats-$chartId-comparison-svg-description">각 항목의 출석률을 가로 막대로 비교합니다.</desc>
                    $bars
                </svg>
                <p class="eb-stats-chart__tooltip" id="stats-$chartId-tooltip" role="tooltip" aria-live="polite" hidden></p>
            </div>
        """.trimIndent()
    }

    private fun chartEmptyHtml(message: String): String = """<p class="eb-stats-chart__empty">$message</p>"""

    private fun horizontalBarPath(x: Double, y: Double, width: Double, height: Double = 20.0): String {
        if (width <= 0.0) return ""
        val right = x + width
        val bottom = y + height
        val radius = minOf(4.0, width, height / 2.0)
        return "M$x,$y H${right - radius} Q$right,$y $right,${y + radius} V${bottom - radius} Q$right,$bottom ${right - radius},$bottom H$x Z"
    }

    private fun verticalBarPath(x: Double, y: Double, width: Double, height: Double): String {
        if (height <= 0.0) return ""
        val right = x + width
        val bottom = y + height
        val radius = minOf(4.0, width / 2.0, height)
        return "M$x,$bottom V${y + radius} Q$x,$y ${x + radius},$y H${right - radius} Q$right,$y $right,${y + radius} V$bottom Z"
    }

    private fun shortDateLabel(value: String): String = value.removePrefix(value.take(5)).ifEmpty { value }

    private fun shortComparisonLabel(value: String): String = if (value.length > 16) "${value.take(15)}…" else value

    private fun dailyTableHtml(rows: List<DailyAttendanceStat>): String = """
        <section class="eb-card">
            <h2>일자별 통계</h2>
            <div class="eb-table-scroll">
                <table class="eb-table">
                    <thead><tr><th scope="col">날짜</th><th scope="col">대상</th><th scope="col">출석 인정</th><th scope="col">출석률</th></tr></thead>
                    <tbody>${rows.joinToString("") { row -> statRowHtml(row.serviceDate, row.stats) }}</tbody>
                </table>
            </div>
        </section>
    """.trimIndent()

    private fun classTableHtml(rows: List<ClassAttendanceStat>): String = """
        <section class="eb-card">
            <h2>반별 통계</h2>
            <div class="eb-table-scroll">
                <table class="eb-table">
                    <thead><tr><th scope="col">반</th><th scope="col">대상</th><th scope="col">출석 인정</th><th scope="col">출석률</th></tr></thead>
                    <tbody>${rows.joinToString("") { row -> statRowHtml(row.className, row.stats) }}</tbody>
                </table>
            </div>
        </section>
    """.trimIndent()

    private fun studentTableHtml(rows: List<StudentAttendanceStat>): String = """
        <section class="eb-card">
            <h2>학생별 통계</h2>
            <div class="eb-table-scroll">
                <table class="eb-table">
                    <thead><tr><th scope="col">학생</th><th scope="col">대상</th><th scope="col">출석 인정</th><th scope="col">출석률</th></tr></thead>
                    <tbody>${rows.joinToString("") { row -> statRowHtml(row.studentName, row.stats) }}</tbody>
                </table>
            </div>
        </section>
    """.trimIndent()

    private fun statRowHtml(label: String, stats: AttendanceStats): String = """
        <tr>
            <td>${label.escapeHtml()}</td>
            <td>${stats.totalCount}</td>
            <td>${stats.attendedCount}</td>
            <td>${attendanceRateLabel(stats)}</td>
        </tr>
    """.trimIndent()

    private fun wireChartTooltips() {
        val marks = document.querySelectorAll("[data-stats-tooltip]")
        for (index in 0 until marks.length) {
            val mark = marks.item(index) as? Element ?: continue
            val message = mark.getAttribute("data-stats-tooltip") ?: continue
            val tooltip = mark.getAttribute("data-stats-tooltip-target")
                ?.let(document::getElementById) as? HTMLElement ?: continue
            val crosshair = mark.getAttribute("data-stats-crosshair")?.let(document::getElementById)
            val crosshairX = mark.getAttribute("data-stats-crosshair-x")

            fun showTooltip() {
                tooltip.textContent = message
                tooltip.removeAttribute("hidden")
                if (crosshair != null && crosshairX != null) {
                    crosshair.setAttribute("x1", crosshairX)
                    crosshair.setAttribute("x2", crosshairX)
                    crosshair.removeAttribute("hidden")
                }
            }

            fun hideTooltip() {
                tooltip.setAttribute("hidden", "")
                crosshair?.setAttribute("hidden", "")
            }

            mark.addEventListener("mouseenter", { showTooltip() })
            mark.addEventListener("mouseleave", { hideTooltip() })
            mark.addEventListener("focus", { showTooltip() })
            mark.addEventListener("blur", { hideTooltip() })
        }
    }

    private fun wireDateRange(session: SessionState, academyId: String) {
        val form = document.getElementById("stats-date-range-form") as? HTMLFormElement ?: return
        form.onsubmit = { event ->
            event.preventDefault()
            val range = DateRange(
                startDate = inputValue("stats-start-date").trim(),
                endDate = inputValue("stats-end-date").trim(),
            )
            val validationError = dateRangeValidationError(range)
            if (validationError != null) {
                showDateRangeError(validationError)
            } else {
                (document.getElementById("stats-date-range-submit") as? HTMLButtonElement)?.disabled = true
                load(session, academyId, range)
            }
            null
        }
    }

    private fun dateRangeValidationError(range: DateRange): String? = when {
        range.startDate.isEmpty() || range.endDate.isEmpty() -> "시작일과 종료일을 모두 선택해 주세요."
        range.startDate > range.endDate -> "종료일은 시작일과 같거나 이후여야 해요."
        else -> null
    }

    private fun inputValue(id: String): String = (document.getElementById(id) as? HTMLInputElement)?.value.orEmpty()

    private fun showDateRangeError(message: String) {
        val error = document.getElementById("stats-date-range-error") ?: return
        error.textContent = message
        error.removeAttribute("hidden")
    }

    private fun attendanceRateLabel(stats: AttendanceStats): String = stats.attendanceRate?.let(::formatPercent) ?: "데이터 없음"

    private fun formatPercent(rate: Double): String {
        val tenths = round(rate * 1_000).toInt()
        return "${tenths / 10}.${tenths % 10}%"
    }

    private fun defaultRange(): DateRange {
        val today = Date()
        val monthStart = Date(today.getFullYear(), today.getMonth(), 1)
        return DateRange(formatDate(monthStart), formatDate(today))
    }

    private fun formatDate(date: Date): String = "${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}"
}
